import React, { useState, useEffect, useRef } from 'react';

// Centillion brand colors
const COLORS = {
  deepNavy: '#0D2145',
  centillionBlue: '#041624',
  steelBlue: '#223B6E',
  violetPurple: '#6057A8',
  coolGray: '#BDBEBE',
  offWhite: '#F6F5F3',
};

// Agent ensemble from Bootstrap spec
const AGENTS = [
  { id: 'cdp-planner', name: 'Planner', status: 'active', load: 0.23 },
  { id: 'cdp-executor', name: 'Executor', status: 'active', load: 0.67 },
  { id: 'cdp-retriever', name: 'Retriever', status: 'idle', load: 0.12 },
  { id: 'cdp-validator', name: 'Validator', status: 'active', load: 0.45 },
  { id: 'openvino-infer', name: 'Edge AI', status: 'active', load: 0.89 },
];

// SLO metrics
const SLOS = {
  handoffSuccess: { value: 99.7, target: 99, unit: '%' },
  handoffLatency: { value: 3.2, target: 5, unit: 's' },
  contextCacheHit: { value: 94.1, target: 90, unit: '%' },
  taskCompletion: { value: 87.3, target: 85, unit: '%' },
};

// Context graph nodes
const CONTEXT_NODES = [
  { id: 'obj-1', type: 'objective', label: 'Audience Monetization', tier: 'HOT', trust: 0.95 },
  { id: 'plan-1', type: 'plan', label: 'CAMARA Verification', tier: 'HOT', trust: 0.92 },
  { id: 'task-1', type: 'task', label: 'SIM Swap Check', tier: 'WARM', trust: 0.88 },
  { id: 'task-2', type: 'task', label: 'Device Location', tier: 'WARM', trust: 0.85 },
  { id: 'result-1', type: 'result', label: 'Trust: 0.91', tier: 'HOT', trust: 0.91 },
  { id: 'handoff-1', type: 'handoff', label: 'A2A → Executor', tier: 'HOT', trust: 0.94 },
];

// Particle background
const ParticleField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    const particles: { x: number; y: number; vx: number; vy: number; size: number; alpha: number }[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 1,
        alpha: Math.random() * 0.5 + 0.1,
      });
    }

    const animate = () => {
      ctx.fillStyle = COLORS.centillionBlue;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(96, 87, 168, ${0.15 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96, 87, 168, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });

      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0" />;
};

// SLO Radial Gauge
const SLOGauge: React.FC<{ metrics: typeof SLOS }> = ({ metrics }) => {
  const health = Object.values(metrics).reduce((acc, m) => {
    const ratio = m.unit === '%' ? m.value / 100 : (m.target - Math.abs(m.value - m.target)) / m.target;
    return acc + Math.min(1, ratio);
  }, 0) / Object.keys(metrics).length;

  const hue = health > 0.9 ? 160 : health > 0.7 ? 45 : 0;

  return (
    <div className="absolute top-6 right-6 w-28 h-28">
      <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
        <circle
          cx="50" cy="50" r="45" fill="none"
          stroke={`hsl(${hue}, 70%, 50%)`}
          strokeWidth="4"
          strokeDasharray={`${health * 283} 283`}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-centillion-white">
        <span className="text-2xl font-semibold font-display tracking-display">{(health * 100).toFixed(0)}</span>
        <span className="text-xs opacity-60 -mt-1">System Health</span>
      </div>
    </div>
  );
};

// Agent Orbit
const AgentOrbit: React.FC<{ agents: typeof AGENTS }> = ({ agents }) => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setRotation((r) => (r + 0.5) % 360), 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute top-1/2 left-20 -translate-y-1/2 w-48 h-48">
      <div className="absolute inset-0 border border-dashed border-centillion-steel/30 rounded-full" />
      {agents.map((agent, i) => {
        const angle = (i / agents.length) * 360 + rotation;
        const rad = (angle * Math.PI) / 180;
        const x = 96 + Math.cos(rad) * 72;
        const y = 96 + Math.sin(rad) * 72;
        const statusColor = agent.status === 'active' ? '#4ade80' : COLORS.coolGray;

        return (
          <div
            key={agent.id}
            className="absolute w-10 h-10 rounded-full flex items-center justify-center transition-all"
            style={{
              left: x - 20,
              top: y - 20,
              background: `radial-gradient(circle at 30% 30%, ${COLORS.steelBlue}, ${COLORS.deepNavy})`,
              border: `2px solid ${statusColor}`,
              boxShadow: agent.status === 'active' ? `0 0 20px ${statusColor}44` : 'none',
            }}
          >
            <div
              className="rounded-full opacity-80"
              style={{
                width: `${agent.load * 100}%`,
                height: `${agent.load * 100}%`,
                background: `radial-gradient(circle, ${COLORS.violetPurple}, transparent)`,
              }}
            />
          </div>
        );
      })}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-centillion-white">
        <div className="text-xs opacity-60">Agent Ensemble</div>
        <div className="text-xl font-semibold font-display">
          {agents.filter((a) => a.status === 'active').length}/{agents.length}
        </div>
      </div>
    </div>
  );
};

// Context Graph Layer
const ContextGraph: React.FC<{ nodes: typeof CONTEXT_NODES; activeNode: typeof CONTEXT_NODES[0] | null; onSelect: (n: typeof CONTEXT_NODES[0]) => void }> = ({ nodes, activeNode, onSelect }) => {
  const typeColors: Record<string, string> = {
    objective: COLORS.violetPurple,
    plan: COLORS.steelBlue,
    task: COLORS.coolGray,
    result: '#4ade80',
    handoff: '#f59e0b',
  };

  return (
    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-4" style={{ perspective: '1000px' }}>
      {nodes.map((node) => {
        const isActive = activeNode?.id === node.id;
        const depth = node.tier === 'HOT' ? 0 : node.tier === 'WARM' ? 20 : 40;
        const color = typeColors[node.type];

        return (
          <div
            key={node.id}
            onClick={() => onSelect(node)}
            className="cursor-pointer backdrop-blur-lg transition-all duration-300"
            style={{
              width: isActive ? 140 : 100,
              padding: '12px 16px',
              background: `linear-gradient(135deg, ${color}22, ${color}44)`,
              border: `1px solid ${color}${isActive ? 'cc' : '66'}`,
              borderRadius: 12,
              transform: `translateZ(${-depth}px) scale(${isActive ? 1.1 : 1})`,
            }}
          >
            <div className="text-xs uppercase tracking-widest mb-1" style={{ color }}>{node.type} • {node.tier}</div>
            <div className="text-xs text-centillion-white">{node.label}</div>
            <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${node.trust * 100}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Voice Indicator
const VoiceIndicator: React.FC<{ listening: boolean }> = ({ listening }) => (
  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-8 py-4 bg-white/5 rounded-full backdrop-blur-xl border border-white/10">
    <div
      className={`w-3 h-3 rounded-full ${listening ? 'animate-pulse' : ''}`}
      style={{
        background: listening ? COLORS.violetPurple : COLORS.coolGray,
        boxShadow: listening ? `0 0 20px ${COLORS.violetPurple}` : 'none',
      }}
    />
    <span className="text-sm text-centillion-white/80">
      {listening ? 'Listening for intent...' : 'Tap or speak to engage'}
    </span>
  </div>
);

// Handoff Stream
const HandoffStream: React.FC = () => {
  const handoffs = [
    { id: 1, from: 'Planner', to: 'Executor', status: 'completed', latency: 2.3 },
    { id: 2, from: 'Executor', to: 'Retriever', status: 'acknowledged', latency: 1.1 },
    { id: 3, from: 'Retriever', to: 'Validator', status: 'pending', latency: null },
  ];
  const statusColors: Record<string, string> = { pending: '#f59e0b', acknowledged: '#3b82f6', completed: '#4ade80', failed: '#ef4444' };

  return (
    <div className="absolute top-40 right-6 w-48">
      <div className="text-xs text-centillion-gray mb-3 uppercase tracking-widest">A2A Handoff Stream</div>
      {handoffs.map((h) => (
        <div key={h.id} className="flex items-center gap-2 py-2 border-b border-white/5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: statusColors[h.status], boxShadow: `0 0 8px ${statusColors[h.status]}` }} />
          <div className="flex-1">
            <div className="text-xs text-centillion-white">{h.from} → {h.to}</div>
            <div className="text-xs text-centillion-gray">{h.status}{h.latency && ` • ${h.latency}s`}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Component
export default function CentillionAmbient2038() {
  const [listening, setListening] = useState(false);
  const [activeNode, setActiveNode] = useState<typeof CONTEXT_NODES[0] | null>(null);
  const [intent, setIntent] = useState<string | null>(null);

  useEffect(() => {
    const intents = ['Analyzing audience segments...', 'Preparing CAMARA verification...', 'Optimizing trust score...', 'Syncing context graph...'];
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIntent(intents[Math.floor(Math.random() * intents.length)]);
        setTimeout(() => setIntent(null), 3000);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-screen h-screen overflow-hidden font-body text-centillion-white">
      <ParticleField />
      
      {/* Brand */}
      <div className="absolute top-6 left-6 z-10">
        <div className="font-display text-2xl font-semibold tracking-display flex items-center gap-2.5">
          <svg width="28" height="28" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="4" fill={COLORS.violetPurple} />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
              <line key={angle} x1="50" y1="50" x2={50 + 40 * Math.cos((angle * Math.PI) / 180)} y2={50 + 40 * Math.sin((angle * Math.PI) / 180)} stroke={COLORS.violetPurple} strokeWidth="2" />
            ))}
          </svg>
          Centillion.ai
        </div>
        <div className="text-xs text-centillion-gray mt-1">The data engine behind tomorrow's AI</div>
      </div>

      <SLOGauge metrics={SLOS} />
      <HandoffStream />
      <AgentOrbit agents={AGENTS} />

      {intent && (
        <div className="absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 px-12 py-6 bg-centillion-violet/15 rounded-2xl backdrop-blur-xl border border-centillion-violet/30 z-20 animate-fadeIn">
          <div className="text-lg font-display tracking-display text-centillion-white">{intent}</div>
        </div>
      )}

      <ContextGraph nodes={CONTEXT_NODES} activeNode={activeNode} onSelect={setActiveNode} />
      <div onClick={() => setListening(!listening)}><VoiceIndicator listening={listening} /></div>
      <div className="absolute bottom-6 right-6 text-xs text-centillion-gray/50">Ambient Intelligence v2038 • Bootstrap Loop v8.3</div>
    </div>
  );
}
